
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, RefreshCw, CheckCircle, XCircle } from 'lucide-react';
import { getTelegramNotificationHistory, resendTelegramNotification } from '@/services/telegramNotificationService';

interface TelegramNotificationHistoryProps {
  procurementId?: string;
  limit?: number;
}

export function TelegramNotificationHistory({ procurementId, limit = 10 }: TelegramNotificationHistoryProps) {
  const [notifications, setNotifications] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'success' | 'failed'>('all');
  const [resendingId, setResendingId] = useState<string | null>(null);

  const fetchNotifications = async () => {
    setIsLoading(true);
    try {
      const filterOptions = {
        success: filter === 'success' ? true : filter === 'failed' ? false : undefined,
      };
      
      const data = await getTelegramNotificationHistory(limit, filterOptions);
      
      // Filter by procurement_id if provided
      const filteredData = procurementId 
        ? data.filter((item: any) => item.procurement_id === procurementId)
        : data;
        
      setNotifications(filteredData);
    } catch (error) {
      console.error('Error fetching notification history:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [procurementId, filter]);

  const handleResend = async (id: string) => {
    setResendingId(id);
    try {
      await resendTelegramNotification(id);
      // Refresh the notification list after resend attempt
      fetchNotifications();
    } finally {
      setResendingId(null);
    }
  };

  const formatDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const truncateMessage = (message: string, maxLength = 100) => {
    return message.length > maxLength
      ? message.substring(0, maxLength) + '...'
      : message;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          Showing {notifications.length} notifications
        </div>
        
        <div className="flex items-center gap-2">
          <Select
            value={filter}
            onValueChange={(value: 'all' | 'success' | 'failed') => setFilter(value)}
          >
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Messages</SelectItem>
              <SelectItem value="success">Successful</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
            </SelectContent>
          </Select>
          
          <Button variant="outline" size="sm" onClick={fetchNotifications}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>
      
      {notifications.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          No notification history found
        </div>
      ) : (
        <div className="space-y-3">
          {notifications.map((notification) => (
            <Card key={notification.id} className={notification.success ? '' : 'border-destructive/50'}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <Badge variant={notification.success ? 'default' : 'destructive'} className="flex items-center gap-1">
                    {notification.success ? (
                      <>
                        <CheckCircle className="h-3 w-3" />
                        Sent
                      </>
                    ) : (
                      <>
                        <XCircle className="h-3 w-3" />
                        Failed
                      </>
                    )}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {formatDate(notification.created_at)}
                  </span>
                </div>
                
                <div className="text-sm bg-muted/50 p-2 rounded-md font-mono">
                  {truncateMessage(notification.message)}
                </div>
                
                <div className="flex items-center justify-between text-xs">
                  <div>
                    <span className="font-semibold">To:</span>{' '}
                    {notification.recipient_type === 'group' ? 'Group Chat' : 'User'} ({notification.recipient_id})
                  </div>
                  
                  {!notification.success && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleResend(notification.id)}
                      disabled={resendingId === notification.id}
                      className="h-7 px-2"
                    >
                      {resendingId === notification.id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : 'Resend'}
                    </Button>
                  )}
                </div>
                
                {notification.error_message && (
                  <div className="text-xs text-destructive mt-1">
                    Error: {notification.error_message}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
